import {
  classEnrollmentFieldIds,
  classEnrollmentGenders,
  type ClassEnrollmentGender,
  type ClassEnrollmentStatus,
} from "@/server/platform/db/schema/class-enrollments";
import { normalizeCampPhone } from "@/modules/camp-registrations/domain/lib/phone";

const ALLOWED: Record<ClassEnrollmentStatus, ClassEnrollmentStatus[]> = {
  pending: ["contacted", "confirmed", "rejected", "duplicate"],
  contacted: ["confirmed", "rejected", "duplicate"],
  confirmed: ["rejected"],
  rejected: [],
  duplicate: ["pending", "contacted", "confirmed"],
  converted: [],
};

export function canTransitionClassEnrollmentStatus(
  from: ClassEnrollmentStatus,
  to: ClassEnrollmentStatus,
): boolean {
  if (from === to) return true;
  return ALLOWED[from]?.includes(to) ?? false;
}

export function isHoneypotTripped(value: string | undefined): boolean {
  return (value ?? "").trim().length > 0;
}

/** Ids the applicant asked for that are not in the currently open set. */
export function closedTemplateIds(
  requested: string[],
  openIds: ReadonlySet<string>,
): string[] {
  return requested.filter((id) => !openIds.has(id));
}

export type EnrollmentProfile = {
  fullName: string;
  nickname: string | null;
  dateOfBirth: string;
  gender: ClassEnrollmentGender;
  phoneNumber: string;
  note: string | null;
};

export function profileFromAnswers(
  answers: Array<{ fieldId: string; value: string | null }>,
):
  | { ok: true; profile: EnrollmentProfile }
  | { ok: false; code: "PROFILE_INCOMPLETE" | "INVALID_GENDER" | "INVALID_PHONE"; fieldId?: string } {
  const valueOf = (fieldId: string) =>
    answers.find((answer) => answer.fieldId === fieldId)?.value?.trim() ?? "";

  const fullName = valueOf(classEnrollmentFieldIds.name);
  const nickname = valueOf(classEnrollmentFieldIds.nickname);
  const dateOfBirth = valueOf(classEnrollmentFieldIds.dateOfBirth);
  const gender = valueOf(classEnrollmentFieldIds.gender);
  const phoneNumber = normalizeCampPhone(valueOf(classEnrollmentFieldIds.phone));
  const note = valueOf(classEnrollmentFieldIds.note);

  if (!fullName) {
    return { ok: false, code: "PROFILE_INCOMPLETE", fieldId: classEnrollmentFieldIds.name };
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
    return {
      ok: false,
      code: "PROFILE_INCOMPLETE",
      fieldId: classEnrollmentFieldIds.dateOfBirth,
    };
  }
  if (!classEnrollmentGenders.includes(gender as ClassEnrollmentGender)) {
    return { ok: false, code: "INVALID_GENDER", fieldId: classEnrollmentFieldIds.gender };
  }
  if (!/^[\d+\-()]{8,30}$/.test(phoneNumber)) {
    return { ok: false, code: "INVALID_PHONE", fieldId: classEnrollmentFieldIds.phone };
  }

  return {
    ok: true,
    profile: {
      fullName,
      nickname: nickname || null,
      dateOfBirth,
      gender: gender as ClassEnrollmentGender,
      phoneNumber,
      note: note || null,
    },
  };
}
